import type { TranslationSchema } from '../types';

export const es: TranslationSchema = {
  nav: {
    features: 'Capacidades',
    dlpVault: 'Bóveda DLP',
    budget: 'Gestión Presupuestaria',
    analytics: 'Analítica',
    architecture: 'Arquitectura',
    comparison: 'Comparación',
    faq: 'Preguntas',
    github: 'GitHub',
    joinWaitlist: 'Unirse a la Lista',
    badge: 'Beta Privada',
    openSourceBadge: 'Código Abierto',
  },
  hero: {
    statusBadge: 'Acceso a Beta Privada con Apertura Mensual',
    openSourceBadge: '100% Código Abierto en GitHub',
    titleLine1: 'La Pasarela de IA Empresarial de Código Abierto con',
    titleHighlight: 'Soberanía de Datos Zero-Trust',
    titleLine2: 'y Control Preciso de Presupuesto',
    description:
      'Permita que sus equipos utilicen cualquier LLM de forma segura. La sanitización DLP en memoria redacta información confidencial y secretos antes de salir de su red, respaldada por presupuestos estrictos en USD con sobregiro suave, SAML SSO y auditoría inmutable.',
    trustPoint1: 'Bóveda de Tokens Efímera en Memoria Sin Almacenamiento',
    trustPoint2: 'Enrutamiento y Respaldo Multi-Proveedor Universal',
    trustPoint3: 'Latencia Submilisegundo en el Edge de Cloudflare',
    githubStars: 'Ver en GitHub',
  },
  waitlist: {
    inputPlaceholder: 'Introduzca su correo corporativo...',
    ctaButton: 'Solicitar Acceso Anticipado',
    submitting: 'Asegurando su lugar...',
    successTitle: '¡Está en la lista!',
    successMessage:
      'Gracias por unirse a nuestra lista de espera de la beta privada. Le notificaremos en la próxima cohorte mensual de acceso.',
    duplicateMessage:
      '¡Ya está registrado! Tenemos su correo en nuestra base y nos comunicaremos en la próxima cohorte.',
    privacyNotice: 'Política estricta de cero spam. Su correo se almacena exclusivamente para invitaciones a la beta.',
    errorMessage: 'No se pudo registrar el correo. Por favor verifique su dirección e inténtelo de nuevo.',
    statsNote: 'Uniéndose a más de 450 equipos de ingeniería y seguridad en espera',
    rolloutNotice: 'Las invitaciones a la beta se envían mensualmente.',
  },
  dlpDemo: {
    badge: 'Demostración Interactiva en Vivo',
    title: 'Experimente la Sanitización DLP Bidireccional',
    subtitle:
      'Las credenciales confidenciales y los datos de clientes se sustituyen por tokens criptográficos en memoria antes de salir de su firewall, y se restauran automáticamente durante la respuesta.',
    samplePromptLabel: 'Cargar Datos Sensibles de Prueba:',
    presets: {
      card: '💳 Tarjeta de Crédito y Facturación',
      apiSecret: '🔑 Clave API y Secreto de Producción',
      pii: '👤 DNI / Documento Nacional y Datos Personales',
    },
    rawInputLabel: '1. Prompt del Usuario (Perímetro Corporativo Seguro)',
    rawInputPlaceholder: 'Escriba un texto con correos, tarjetas de crédito o claves API...',
    sanitizedLabel: '2. Prompt Sanitizado (Enviado al Proveedor de LLM Externo)',
    sanitizedSubtext: 'Datos sensibles sustituidos por tokens criptográficos subrogados',
    restoredLabel: '3. Respuesta del LLM (Restaurada en Navegador / App)',
    restoredSubtext: 'El motor de streaming dinámico restaura el contexto original para el usuario',
    vaultStatus: 'Estado de la Bóveda en Memoria: ACTIVA (Efímera, Nunca Persistida)',
    redactedBadge: 'Detectado & Redactado',
    zeroStorageBadge: 'Garantía 100% Sin Fugas',
  },
  budgetSimulator: {
    badge: 'Garantías Financieras',
    title: 'Motor de Presupuesto en USD y Sobregiro Suave',
    subtitle:
      'Aplique límites estrictos por equipo en USD canónico. Si una petición supera ligeramente el saldo restante, Workouse permite completar la respuesta, registra el saldo negativo y detiene nuevos gastos hasta la recarga.',
    teamBudgetLabel: 'Límite Mensual del Equipo (USD):',
    teamBudgetSub: 'Límites canónicos denominados en USD (mín. $5.00) con conversión automática de divisas.',
    currencyLabel: 'Moneda de Visualización:',
    currentBalanceLabel: 'Saldo Restante',
    spentLabel: 'Total Gastado en el Ciclo',
    simulatePromptButton: '⚡ Simular Petición de $0.05',
    simulateOverdraftButton: '🚨 Activar Sobregiro Suave (Petición de $0.20)',
    resetButton: 'Reiniciar Simulación',
    overdraftBannerTitle: 'Sobregiro Suave Activado (Saldo Negativo Registrado)',
    overdraftBannerDesc:
      'La solicitud se completó para no interrumpir el trabajo del usuario. El saldo ahora es negativo; las solicitudes posteriores están bloqueadas hasta reponer presupuesto.',
    statusActive: 'ACTIVO (Dentro del Presupuesto)',
    statusOverdraft: 'BLOQUEADO POR SOBREGIRO (Requiere Recarga)',
    ruleMinLimit: 'Presupuesto Mínimo por Equipo: $5.00 USD',
    ruleSoftOverdraft: 'Sobregiro Suave: Finaliza el turno, registra saldo negativo, detiene nuevos prompts',
  },
  analytics: {
    badge: 'Observabilidad y Auditoría',
    title: 'Analítica Inmutable de Tokens y Costes',
    subtitle:
      'Cada token consumido a través de la pasarela se registra con atribución criptográfica. Desglose de costes en tiempo real para OpenAI, Claude y modelos locales.',
    metrics: {
      tokens: 'Total de Tokens Procesados',
      spend: 'Gasto Mensual en Modelos',
      dlpBlocked: 'Entidades PII Redactadas',
      latency: 'Latencia Media de Pasarela',
    },
    liveAuditTitle: 'Flujo de Auditoría Inmutable Append-Only',
    liveAuditSub: 'Los eventos de auditoría se registran directamente en PostgreSQL con garantía de no alteración.',
    tableHeaders: {
      time: 'Hora',
      model: 'Modelo / Pasarela',
      team: 'Equipo / Usuario',
      tokens: 'Tokens (Entrada/Salida)',
      cost: 'Coste (USD)',
      dlpFlag: 'Acción DLP',
    },
    modelShareTitle: 'Distribución de Coste por Proveedor',
    immutableNotice: 'Arquitectura 100% Append-Only: Los registros nunca se pueden modificar ni eliminar.',
  },
  features: {
    badge: 'Arquitectura Empresarial',
    title: 'Diseñado para Oficiales de Seguridad, Amado por Ingenieros',
    subtitle:
      'Un plano de control de IA integral construido con contratos estrictos de programación funcional, cero fugas de datos y límites financieros infranqueables.',
    items: [
      {
        id: 'dlp',
        tag: 'Soberanía de Datos',
        title: 'Bóveda de Tokens DLP en Memoria',
        description:
          'Las reglas regex de Nivel 2 de su organización se ejecutan junto con detectores universales (tarjetas, DNI, claves AWS). Los tokens residen únicamente en memoria volátil por petición.',
        specs: ['Reglas Nivel 2 Tienen Prioridad', 'Desanonimizador Dinámico en Cliente', 'Cero Fugas en DB / Logs'],
      },
      {
        id: 'budget',
        tag: 'Garantías Financieras',
        title: 'Motor de Presupuesto Preciso en USD',
        description:
          'Aplique límites estrictos por equipo en USD canónico (límite mín. $5.00). El manejo suave de sobregiro completa la solicitud actual, registra el saldo negativo y detiene nuevos gastos.',
        specs: ['Cuotas Canónicas en USD', 'Visualización en Moneda Local (EUR/USD/GBP)', 'Contabilidad de Costos en Tiempo Real'],
      },
      {
        id: 'gateway',
        tag: 'Alta Disponibilidad',
        title: 'Enrutador Unificado de Pasarela de IA',
        description:
          'Conmutación por error en cascada automática: Pasarela de Equipo → Pasarela de Org → Predeterminada de Plataforma. Soporte nativo para Portkey, Cloudflare AI Gateway, OpenAI, Anthropic y Ollama.',
        specs: ['Cifrado de Claves AES-256-GCM', 'Cascada Inteligente de Respaldo', 'Analítica de Latencia y Tokens'],
      },
      {
        id: 'rag',
        tag: 'Base de Conocimiento',
        title: 'Extractor Asíncrono y Motor RAG',
        description:
          'Microservicio en Haskell Parsec que extrae contenido de PDF/DOCX/Markdown a AST de forma asíncrona. Vectores estrictamente aislados por ID de organización en Qdrant.',
        specs: ['100% Procesamiento Asíncrono', 'Subida Directa Firmada a R2/S3', 'Particionado Estricto de Vectores'],
      },
      {
        id: 'iam',
        tag: 'Identidad y Cumplimiento',
        title: 'IAM Empresarial y Auditoría',
        description:
          'Federación de identidades con NextAuth v5, compatible con SAML Jackson, LDAP y OAuth con selector de organización multi-inquilino. Registros inmutables por cada token.',
        specs: ['Soporte SAML 2.0 y LDAPts', 'Selector de Organización Post-Login', 'Registro de Auditoría Inmutable'],
      },
      {
        id: 'deployment',
        tag: 'Flexibilidad de Despliegue',
        title: 'Cloudflare SaaS o Auto-Hospedado',
        description:
          'Ejecútelo como SaaS multi-inquilino en Cloudflare Pages + Tunnel + R2, o despliéguelo en su infraestructura interna con Docker Compose completamente aislado.',
        specs: ['Túnel Zero-Trust de Cloudflare', 'Modo Monoinquilino Docker Compose', 'Capa DB Edge con PostgREST'],
      },
      {
        id: 'connectors',
        tag: 'Extensibilidad',
        title: 'Plugins Conectores y Protocolo WAP',
        description:
          'El Protocolo de Acción Workouse (WAP) conecta aplicaciones corporativas como contabilidad Paraşüt, sistemas CRM y endpoints REST OpenAPI 3.0 de forma segura.',
        specs: ['Workouse Action Protocol (WAP)', 'Plugins listos para Paraşüt y CRM', 'Auto-Scaffolding OpenAPI 3.0'],
      },
      {
        id: 'sandbox',
        tag: 'Ejecución Segura',
        title: 'Sandboxes V8 Aislados y Flujos Reutilizables',
        description:
          'Ejecute scripts de agentes en sandboxes aislados de Cloudflare V8 / Deno. La simulación HITL en dos fases muestra diferencias visuales (diff) antes de confirmar cambios de estado.',
        specs: ['Cloudflare V8 / Deno Aislado', 'Simulación HITL en Dos Fases', 'Apps de Flujo Guardadas y Slash Commands'],
      },
      {
        id: 'secrets',
        tag: 'Seguridad Autorizada',
        title: 'Arquitectura Autorizada de Secretos',
        description:
          'Cero ingreso de credenciales en bruto. HashiCorp Vault en on-prem y Cloudflare Secrets en edge garantizan que modelos y sandboxes nunca accedan a tokens sin cifrar.',
        specs: ['HashiCorp Vault y Cloudflare Secrets', 'Cero Ingreso de Claves en Bruto', 'Salida Proxy Broker'],
      },
    ],
  },
  architecture: {
    badge: 'Topología de Flujo de Datos',
    title: 'Cómo Protege Workouse AI a su Organización',
    subtitle:
      'Los textos reales permanecen en su perímetro de PostgreSQL. Solo los tokens subrogados sanitizados salen de su red.',
    step1Title: '1. Envío del Prompt del Usuario',
    step1Desc: 'El colaborador envía una consulta mediante la interfaz web, extensión de IDE o API autenticada por SAML.',
    step2Title: '2. Sanitización DLP en Memoria',
    step2Desc: 'La Bóveda de Tokens efímera detecta datos sensibles y los reemplaza al instante por tokens ({{TAG_N}}).',
    step3Title: '3. Salida Cifrada por la Pasarela',
    step3Desc: 'La petición sanitizada se enruta mediante la pasarela hacia OpenAI, Anthropic o Cloudflare AI.',
    step4Title: '4. Re-anonimización Dinámica en Respuesta',
    step4Desc: 'La respuesta del LLM se reensambla dinámicamente en streaming y los tokens se borran de la memoria RAM.',
    securityNote: 'Ningún dato PII llega a los proveedores de modelos externos. Las claves secretas nunca se escriben en disco.',
  },
  comparison: {
    badge: 'Comparativa Empresarial',
    title: 'Por Qué el Acceso Directo a LLMs Incumple Normativas',
    subtitle:
      'Compruebe cómo Workouse AI proporciona la seguridad, gobernanza y control financiero que las suscripciones estándar no ofrecen.',
    headers: {
      feature: 'Capacidad',
      direct: 'Cuentas Directas LLM',
      genericWrapper: 'Wrappers de IA Genéricos',
      workouse: 'Pasarela Workouse AI',
    },
    rows: [
      {
        feature: 'Redacción DLP Bidireccional en Memoria',
        direct: 'Ninguna (Fuga Total de Datos)',
        genericWrapper: 'Regex básico / Guardado en DB',
        workouse: 'Bóveda Efímera en Memoria (Cero Fugas)',
        highlight: true,
      },
      {
        feature: 'Control de Presupuesto y Sobregiro por Equipo',
        direct: 'Solo a nivel de cuenta global',
        genericWrapper: 'Facturación mensual manual',
        workouse: 'Límites Atómicos USD + Sobregiro Suave',
        highlight: true,
      },
      {
        feature: 'Enrutamiento y Respaldo Multi-Modelo',
        direct: 'Bloqueo a un solo proveedor',
        genericWrapper: 'Lista fija de modelos',
        workouse: 'Portkey + Cloudflare + Directo + Ollama',
      },
      {
        feature: 'SSO Empresarial e IAM',
        direct: 'Complemento costoso $$$',
        genericWrapper: 'Solo Correo / Contraseña',
        workouse: 'SAML 2.0, LDAP y Selector Post-Login',
      },
      {
        feature: 'Despliegue Auto-Hospedado Aislado',
        direct: 'No disponible',
        genericWrapper: 'Solo en la Nube',
        workouse: 'Stack Completo Docker Compose Air-Gapped',
      },
      {
        feature: 'Auditoría Inmutable de Tokens y Costos',
        direct: 'Historial básico de API',
        genericWrapper: 'Panel de control simple',
        workouse: 'Registro Criptográfico Inmutable',
      },
    ],
  },
  cta: {
    badge: 'Beta Privada Exclusiva',
    title: 'Proteja los Flujos de IA de su Empresa Hoy Mismo',
    description:
      'Únase a líderes de ingeniería y seguridad en finanzas, tecnología y salud que ya implementan IA conforme a normativas con Workouse.',
    button: 'Unirse a la Lista de Espera',
    disclaimer: 'No se requiere tarjeta de crédito. Las invitaciones se envían mensualmente.',
    githubButton: 'Explorar en GitHub',
  },
  faq: {
    badge: 'Preguntas Frecuentes',
    title: 'Todo lo que Necesita Saber',
    subtitle: '¿Tiene preguntas sobre el despliegue, la seguridad o el acceso a la beta privada mensual?',
    items: [
      {
        question: '¿Es Workouse AI de código abierto?',
        answer:
          '¡Sí! Workouse AI es de código abierto (open-core) y completamente transparente. La pasarela central, el tokenizador DLP en memoria y el motor contable de presupuesto están disponibles en GitHub (github.com/workouse). Puede alojarlo localmente con Docker Compose o ejecutar la edición edge en Cloudflare.',
      },
      {
        question: '¿Cómo evita la Bóveda DLP que los datos se filtren a los proveedores de LLM?',
        answer:
          'Antes de que cualquier solicitud abandone su perímetro, nuestro tokenizador en memoria analiza el prompt con reglas de Nivel 1 (tarjetas, DNI, tokens de API) y Nivel 2 (regex personalizadas de su empresa). Las cadenas sensibles se sustituyen por tokens efímeros (ej. {{CREDIT_CARD_1}}). Esta asignación existe únicamente en la memoria RAM y se destruye inmediatamente tras la desanonimización de la respuesta.',
      },
      {
        question: '¿Se puede desplegar Workouse AI 100% on-premises en nuestra nube privada?',
        answer:
          '¡Sí! Workouse AI soporta dos modos: el Modo A es un entorno monoinquilino con Docker Compose para VPCs privadas o redes aisladas (air-gapped). El Modo B es una arquitectura SaaS multi-inquilino de alto rendimiento basada en Cloudflare Pages + Tunnel + R2.',
      },
      {
        question: '¿Cómo funciona la protección de sobregiro suave (soft-overdraft)?',
        answer:
          'A los equipos se les asignan presupuestos canónicos en USD (mínimo $5.00). Si una petición supera ligeramente el saldo restante (ej. coste de $0.20 cuando quedan $0.05), Workouse permite que la respuesta termine fluidamente, registra el saldo negativo (-$0.15) y bloquea nuevos prompts hasta que se recargue el presupuesto.',
      },
      {
        question: '¿Qué pasarelas y proveedores de LLM son compatibles?',
        answer:
          'Workouse AI se integra de forma nativa con Portkey AI Gateway, Cloudflare AI Gateway, APIs directas (OpenAI, Anthropic, Google Gemini) y modelos locales de código abierto mediante Ollama / vLLM.',
      },
      {
        question: '¿Con qué frecuencia se envían las invitaciones para la beta privada?',
        answer:
          'Admitimos cohortes de usuarios mensualmente. Tienen prioridad las solicitudes con dominios corporativos y requisitos de colaboración en equipo.',
      },
    ],
  },
  seo: {
    title: 'Workouse AI Gateway — Pasarela de IA Empresarial Zero-Trust & Bóveda DLP',
    description:
      'Implemente Workouse AI: la pasarela de IA empresarial zero-trust con bóveda de tokens DLP en memoria RAM, límites de presupuesto en USD y enrutamiento multimodelo. Únase a la lista de espera beta privada.',
    keywords:
      'Pasarela de IA, IA Empresarial, Prevención de Pérdida de Datos, Tokenización DLP, Proxy LLM, Control de Costes IA, Soberanía de Datos, Pasarela de IA Autoalojada, Proxy OpenAI',
    ogTitle: 'Workouse AI Gateway — Pasarela de IA Empresarial Zero-Trust',
    ogDescription:
      'Soberanía de datos, tokenización DLP efímera en memoria y controles de presupuesto en USD para flujos de trabajo de IA empresarial. Autoalojable o en la nube.',
  },
  footer: {
    tagline: 'Pasarela de IA Empresarial de Código Abierto con Soberanía de Datos Zero-Trust.',
    dataSovereignty: 'Seguridad Empresarial • Arquitectura preparada para ISO 27001 / SOC 2',
    openSourceNote: '100% Código Abierto en github.com/workouse',
    rights: 'Todos los derechos reservados.',
    links: {
      architecture: 'Arquitectura',
      github: 'GitHub',
      docs: 'Documentación',
      privacy: 'Política de Privacidad',
      terms: 'Términos de Servicio',
    },
  },
};

